import React, { useState, useEffect } from "react";
import { Select, Input, message, Spin, Button, Modal, Tooltip } from "antd";
import { http } from "../../../../services/http";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Trash, Plus } from "lucide-react";
import { CloseOutlined } from "@ant-design/icons";
import { numberWithSpacesIntl } from "../../../../utils/numberWithSpaces";
import { useSelector } from "react-redux";

function CreateInventoryTransferRequest({ onClose, visible }) {
  const { t } = useTranslation();
  const { warehouse } = useSelector((state) => state.main) || "";

  const [docDate, setDocDate] = useState(moment().format("YYYY-MM-DD"));
  const [items, setItems] = useState([
    { itemCode: "", quantity: "", whsOnHand: "", uomName: "", packageQty: "" },
  ]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState({
    fdata: false,
  });
  const [data, setData] = useState({
    fdata: [],
    wdata: [],
  });
  const [responsible, setResponsible] = useState("");
  const [responsibleData, setResponsibleData] = useState([]);
  const [fromWhs, setFromWhs] = useState("");
  const [toWhs, setToWhs] = useState("");

  const { fdata, wdata } = data;

  useEffect(() => {
    const fetchAllData = async () => {
      setLoadingData({ fdata: true });
      await Promise.all([
        fetchData(),
        fetchWarehouse(),
        fetchResponsibleManagers(),
      ]);
      setLoadingData({ fdata: false });
    };
    fetchAllData();
  }, [visible]);

  const fetchData = async () => {
    try {
      const { data } = await http.get(
        `api/items/ombordagiitems?pageSize=10000`,
      );
      console.log(data);
      const mappedItems = data.map((item) => ({
        code: item.itemCode,
        name: item.itemName,
        quantityOnStock: item.quantityOnStock,
        quantityOrderedFromVendors: item.quantityOrderedFromVendors,
        quantityOrderedByCustomers: item.quantityOrderedByCustomers,
        ...item,
      }));
      setData((prevState) => ({
        ...prevState,
        fdata: mappedItems,
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchWarehouse = async () => {
    try {
      const { data } = await http.get(`api/warehouses`);

      const filteredItems = data.filter((item) => item.warehouseCode !== "01");

      const mappedItems = filteredItems.map((item) => ({
        code: item.warehouseCode,
        name: item.warehouseName,
      }));
      setData((prevState) => ({
        ...prevState,
        wdata: mappedItems,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const fetchResponsibleManagers = async () => {
    try {
      const { data } = await http.get(
        `api/sales/warehouse-employee-info?skip=0`,
      );
      setResponsibleData(data.data);
    } catch (err) {
      console.log(err);
    }
  };

  const resetForm = () => {
    setDocDate(moment().format("YYYY-MM-DD"));
    setItems([
      {
        itemCode: "",
        quantity: "",
        whsOnHand: "",
        uomName: "",
        packageQty: "",
      },
    ]);
    setResponsible("");
    setFromWhs("");
    setToWhs("");
  };

  const handleSelectChange = (index, value) => {
    const selectedItem = fdata?.find((item) => item.name === value);
    const newItems = [...items];
    const whsOnHand =
      selectedItem?.itemWarehouseInfo?.find(
        (item) => item.warehouseCode === fromWhs,
      )?.inStock -
      selectedItem?.itemWarehouseInfo?.find(
        (item) => item.warehouseCode === fromWhs,
      )?.committed;
    console.log("selectedItem = ", selectedItem);
    newItems[index].itemCode = selectedItem.code;
    newItems[index].whsOnHand = whsOnHand;
    newItems[index].packageQty = selectedItem.packageQty;
    newItems[index].uomName = selectedItem.uomName;
    setItems(newItems);
  };

  const handleQuantityChange = (index, value) => {
    const newItems = [...items];
    newItems[index].quantity = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        itemCode: "",
        quantity: "",
        whsOnHand: "",
        uomName: "",
        packageQty: "",
      },
    ]);
  };

  const removeItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const postData = async () => {
    if (items.some((item) => !item.itemCode || !item.quantity)) {
      message.error(t("fillForm"));
      return;
    }

    setLoading(true);
    try {
      await http.post("api/sales/create-inventory-transfer-request", {
        toWarehouse: toWhs,
        fromWarehouse: fromWhs,
        docDate: docDate,
        responsiblePersonId: responsible,
        stockTransferLines: items.map((item, index) => ({
          itemCode: item.itemCode,
          quantity: Number(item.quantity),
          lineNum: index,
        })),
      });
      message.success(t("sucCreated"));

      setTimeout(() => {
        resetForm();
        onClose();
      }, 800);
    } catch (error) {
      message.error(t("errCreation") + error.response.data.message);
      console.log("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <h1 className="font-nunito text-xl font-extrabold text-[#000000]">
          {t("order")}
        </h1>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      closeIcon={<CloseOutlined />}
      width="75%"
      style={{ height: "70%" }}
      bodyStyle={{ height: "calc(100% - 55px)", overflowY: "auto" }}
    >
      <div className="w-full px-4 sm:px-14">
        <div className="w-full">
          <div className="mt-10 flex w-full flex-col items-center gap-5 sm:flex-row">
            <div className="flex w-full flex-col items-center gap-5 sm:flex-row">
              <div className="flex w-full flex-col gap-3 sm:w-auto">
                <p>{t("from-whs")}</p>

                <Select
                  value={fromWhs}
                  className="h-10 w-full items-center justify-center sm:w-[250px]"
                  options={data.wdata
                    .filter((whs) => {
                      return whs.code !== toWhs;
                    })
                    .map((whs) => {
                      return { value: whs.code, label: whs.name };
                    })}
                  onChange={(val) => setFromWhs(val)}
                />
              </div>

              <div className="flex w-full flex-col gap-3 sm:w-auto">
                <p>{t("toWhs")}</p>
                <Select
                  value={toWhs}
                  className="h-10 w-full items-center justify-center sm:w-[250px]"
                  options={data.wdata
                    .filter((whs) => {
                      return whs.code !== fromWhs;
                    })
                    .map((whs) => {
                      return { value: whs.code, label: whs.name };
                    })}
                  onChange={(val) => setToWhs(val)}
                />
              </div>

              <div className="flex w-full flex-col gap-3 sm:w-auto">
                <p className="font-nunito">{t("date")}</p>
                <Input
                  type="date"
                  value={docDate}
                  className="h-10 w-full sm:w-auto"
                  onChange={(e) => setDocDate(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="mt-10 flex flex-col items-start gap-3 sm:flex-row sm:items-end">
            <div className="flex w-full flex-col gap-2 sm:w-auto">
              {items.map((item, index) => (
                <div className="flex w-full flex-col items-center gap-5 sm:flex-row">
                  <div className="flex w-full flex-col gap-3 sm:w-auto">
                    {index === 0 ? <p>{t("item")}</p> : ""}
                    <Spin spinning={loadingData.fdata}>
                      <Select
                        value={
                          item.itemCode
                            ? fdata?.find((i) => i.code === item.itemCode)?.name
                            : undefined
                        }
                        disabled={!fromWhs}
                        placeholder={t("choose")}
                        className="h-10 w-full items-center justify-center !text-sm sm:w-[500px]"
                        onChange={(value) => handleSelectChange(index, value)}
                        showSearch
                      >
                        {fdata
                          .filter(
                            (i) =>
                              i.name !== null &&
                              !items.some((j) => j.itemCode === i.code),
                          )
                          .map((i) => (
                            <Select.Option key={i.code} value={i.name}>
                              <Tooltip title={i.name}>
                                <span className="text-sm"> {i.name}</span>
                              </Tooltip>
                            </Select.Option>
                          ))}
                      </Select>
                    </Spin>
                  </div>

                  <div className="flex w-full flex-col gap-3 sm:w-auto">
                    {index === 0 ? <p>{t("quantity")}</p> : ""}
                    <Input
                      value={item.quantity}
                      onChange={(e) =>
                        handleQuantityChange(index, e.target.value)
                      }
                      disabled={!fromWhs}
                      type="text"
                      placeholder={t("quantity")}
                      className={`h-10 w-full items-center justify-center border-[1px] ${item.quantity > item.whsOnHand ? "border-red-500" : "border-[#D0D0D0]"} sm:w-[100px]`}
                    />
                  </div>
                  <div className="flex w-full flex-col gap-3 sm:w-auto">
                    {index === 0 ? <p>{t("measureUnit")}</p> : ""}
                    <Input
                      value={item.uomName}
                      disabled={true}
                      type="text"
                      placeholder={t("whsOnHand")}
                      className="h-10 w-full items-center justify-center border-[1px] border-[#D0D0D0] sm:w-[100px]"
                    />
                  </div>
                  <div className="flex w-full flex-col gap-3 sm:w-auto">
                    {index === 0 ? <p>{t("packageQty")}</p> : ""}
                    <Input
                      value={numberWithSpacesIntl(
                        item.quantity / item.packageQty,
                      )}
                      disabled={true}
                      type="text"
                      placeholder={t("whsOnHand")}
                      className="h-10 w-full items-center justify-center border-[1px] border-[#D0D0D0] sm:w-[150px]"
                    />
                  </div>
                  <div className="flex w-full flex-col gap-3 sm:w-auto">
                    {index === 0 ? <p>{t("whsOnHand")}</p> : ""}
                    <Input
                      value={numberWithSpacesIntl(item.whsOnHand)}
                      disabled={true}
                      type="text"
                      placeholder={t("whsOnHand")}
                      className="h-10 w-full items-center justify-center border-[1px] border-[#D0D0D0] sm:w-[180px]"
                    />
                  </div>
                  <div
                    className={`flex gap-2 ${index === 0 ? "sm:mt-8" : "sm:mt-1"} flex-row-reverse`}
                  >
                    {items.length - 1 === index && (
                      <div
                        className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-[1px] bg-[#0A4D68] text-white`}
                      >
                        <Plus
                          // className={`p-1`}
                          onClick={() => addItem()}
                          size={20}
                        />
                      </div>
                    )}
                    {items.length > 1 && (
                      <div
                        className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-[1px] border-red-600 bg-red-500 text-white`}
                      >
                        <Trash
                          // className={`p-1`}
                          onClick={() => removeItem(index)}
                          size={20}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-14 flex w-full flex-col gap-3 sm:w-auto">
            <p>{t("responsiblePerson")}</p>
            <Select
              value={responsible}
              className="h-10 w-full items-center justify-center sm:w-[250px]"
              options={responsibleData.map((item) => {
                return {
                  value: item.employeeId,
                  label: `${item.firstName} ${item.lastName}`,
                };
              })}
              onChange={(val) => setResponsible(val)}
            />
          </div>

          <div className="mt-14">
            <Button
              onClick={postData}
              className="h-12 w-full rounded-3xl bg-[#0A4D68] text-white sm:w-[350px]"
              disabled={
                loading ||
                !fromWhs ||
                !toWhs ||
                !responsible ||
                !items.every((i) => i.itemCode) ||
                !items.every((i) => i.quantity) ||
                items.some((i) => i.quantity > i.whsOnHand) ||
                !(fromWhs === warehouse || toWhs === warehouse)
              }
              loading={loading}
            >
              {loading ? t("saving") : t("saveAndCreate")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default CreateInventoryTransferRequest;
